import { createFileRoute, Link } from "@tanstack/react-router";
import {
  useCitedPagesQuery,
  useQuestionCandidatesQuery,
  useQuestionQuery,
  useQuestionRatingsQuery,
  useQuestionSourcesQuery,
  useQuestionUsageQuery,
  useResumeQuestionMutation,
  useSaveRatingMutation,
} from "@/lib/queries";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AnswerView } from "@/components/answer/AnswerView";
import { RatingPanel } from "@/components/answer/RatingPanel";
import { Discussion } from "@/components/answer/Discussion";
import { CitationCheckTable } from "@/components/answer/CitationCheckTable";
import {
  DownloadDocs,
  OptimizedQuestions,
  OriginalQuestion,
  TokenUsage,
} from "@/components/answer/QuestionPanels";
import { Page, PageHeader, PageScroll, Rail } from "@/components/layout/Page";

export const Route = createFileRoute("/questions/$questionId")({
  component: QuestionDetailPage,
});

function QuestionDetailPage() {
  const { questionId } = Route.useParams();
  const question = useQuestionQuery(questionId);
  const saveRating = useSaveRatingMutation(questionId);
  const resume = useResumeQuestionMutation(questionId);

  // The retrieval audit is only fetched once the answer exists — before that
  // there is nothing to show, and a poll on an empty question is wasted.
  const sources = useQuestionSourcesQuery(questionId, question.data?.is_answered === true);

  // The page images behind the citations. Fetched alongside rather than before
  // the answer renders: the prose is readable immediately and the evidence
  // thumbnails fill in, instead of the whole page waiting on a set of signed
  // URLs.
  const citedPages = useCitedPagesQuery(questionId, question.data?.is_answered === true);

  // Ratings and the discussion are only meaningful once there is an answer to
  // rate or argue with.
  const ratings = useQuestionRatingsQuery(questionId, question.data?.is_answered === true);

  // The optimiser's rewrites and what the run cost. Both are already recorded
  // and neither was ever shown; both are gated on the answer for the same
  // reason as the rest — before then the rows may not exist, and polling for
  // them is a request a second for nothing.
  const candidates = useQuestionCandidatesQuery(questionId, question.data?.is_answered === true);
  const usage = useQuestionUsageQuery(questionId, question.data?.is_answered === true);

  const back = (
    <Link
      to="/questions"
      className="text-xs text-chrome/60 underline-offset-2 transition-colors duration-100 ease-snap hover:text-chrome hover:underline"
    >
      ← Previous questions
    </Link>
  );

  if (question.isLoading) {
    return (
      <Page>
        <PageHeader eyebrow={back} title="Loading…" />
      </Page>
    );
  }
  if (question.isError || !question.data) {
    return (
      <Page>
        <PageHeader
          eyebrow={back}
          title="Not available"
          description="This question does not exist, or is private to someone else."
        />
      </Page>
    );
  }

  const q = question.data;

  return (
    <Page>
      <PageHeader
        eyebrow={back}
        title={q.title}
        description={
          <>
            Asked by {q.created_by} · {new Date(q.created_at).toLocaleString()}
            {q.is_private && " · private"}
            {/* ERIKA carried this in the Original Question header. Without it a
                rating is a score with nothing to attribute it to. */}
            {q.core_prompt_name && (
              <>
                {" · "}
                <span title="The prompt that produced this answer">
                  {q.core_prompt_name}
                  {q.prompt_version ? ` v${q.prompt_version}` : ""}
                </span>
              </>
            )}
          </>
        }
        // Every unique source the answer cited, in one click. ERIKA's
        // Download Docs — the reader who wants the underlying PDFs wants all
        // of them, not one at a time from eight citation links.
        actions={q.answer ? <DownloadDocs answer={q.answer} /> : undefined}
      />

      <PageScroll className="space-y-4">
        {/* A question opened while it is still running resolves itself: the
            query polls until the status changes. */}
        {(q.status === "running" || q.status === "pending") && (
          <Card className="p-5">
            <p className="text-sm">
              This question is still being answered
              {q.stage && <span className="text-muted-foreground"> — {q.stage.replace(/_/g, " ")}</span>}.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Answers take 30–90 seconds. This page updates on its own.
            </p>
            {/* If the runner died mid-answer the operation is still healthy at
                the gateway, and collecting it costs nothing — far better than
                re-asking and paying twice. */}
            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              disabled={resume.isPending}
              onClick={() => resume.mutate()}
            >
              {resume.isPending ? "Checking…" : "Check for the answer now"}
            </Button>
            {resume.isError && (
              <p className="mt-2 text-xs text-muted-foreground">
                Still working. Try again in a moment.
              </p>
            )}
          </Card>
        )}

        {q.status === "failed" && (
          <Card className="p-5">
            <p className="text-sm text-danger-text">This question could not be answered.</p>
            {q.error_message && (
              <p className="mt-1 text-sm text-muted-foreground">{q.error_message}</p>
            )}
            {q.error_code && (
              <p className="mt-2 font-mono text-xs text-muted-foreground">{q.error_code}</p>
            )}
          </Card>
        )}

        {/*
          The answer on the left, the audit trail on the right.

          Everything in the rail answers "where did this come from" — the
          wording that was actually asked, what retrieval looked at, what the
          optimizer rewrote, what the run cost. Stacked underneath, as they
          were, they turned a two-screen answer into a six-screen page and the
          discussion at the bottom of it was effectively unreachable. Beside
          it, they are all visible while the answer is being read, which is
          when anybody wants them.
        */}
        {!q.answer && <OriginalQuestion question={q.question} />}

        {q.answer && (
          <div className="gap-4 xl:grid xl:grid-cols-[minmax(0,1fr)_24rem] xl:items-start">
            <div className="flex min-w-0 flex-col gap-4">
              <AnswerView answer={q.answer} citedPages={citedPages.data?.pages} />
            </div>

            <Rail className="mt-4 xl:mt-0">
              {/* The title above is what the model wrote; this is what was asked.
                  When an answer looks wrong the original wording is the first thing
                  anybody reaches for, so it is a panel rather than a line of muted
                  text under the heading. */}
              <OriginalQuestion question={q.question} />

              {/* Six dimensions, not one. "Was this useful" cannot express
                  "accurate but it answered half the question", which is the most
                  common thing a reviewer actually needs to record. */}
              <RatingPanel
                view={ratings.data}
                saving={saveRating.isPending}
                error={
                  saveRating.isError
                    ? saveRating.error instanceof ApiError
                      ? saveRating.error.message
                      : "That rating could not be saved."
                    : undefined
                }
                onSave={(rating) => saveRating.mutate(rating)}
              />

              {/* Rating and discussion sit with the question, beside the answer
                  rather than below it: under a long answer they were a scroll
                  away from the thing being rated, and easy never to reach. */}
              <Discussion questionId={q.id} isPrivate={q.is_private} />

              {/* Which specific claims the independent checker could not tie back
                  to a source. The verdict above is the summary; this is the part
                  a reader can act on. */}
              {q.citation_check && (
                <CitationCheckTable check={q.citation_check} verdict={q.citation_verdict} />
              )}

              {/* What the retrieval actually selected. Keeping it is what makes an
                  answer auditable after the fact — you can see the pages the model
                  was shown, not only the ones it chose to cite. */}
              {sources.data && sources.data.sources.length > 0 && (
                <details className="rounded-card border border-border bg-card p-5 shadow-panel">
                  <summary className="cursor-pointer text-sm font-medium">
                    Sources searched ({sources.data.sources.length})
                  </summary>
                  <ul className="mt-3 space-y-1 text-sm">
                    {sources.data.sources.map((s) => (
                      <li key={`${s.document_id}-${s.rank}`} className="flex justify-between gap-3">
                        <Link
                          to="/library/$documentId"
                          params={{ documentId: s.document_id }}
                          className="truncate text-primary hover:underline"
                        >
                          {s.document_title}
                          {s.page_number !== null && `, p. ${s.page_number}`}
                        </Link>
                        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                          {s.score.toFixed(3)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {/* What the question became before it was searched. The answer
                  names the candidate it answered; this is the rest of the net. */}
              {candidates.data && <OptimizedQuestions candidates={candidates.data.candidates} />}

              {/* What the run cost, by stage. Summed from activity_events, which
                  holds no text — so there is nothing here to judge before showing
                  it to whoever can already see the question. */}
              {usage.data && <TokenUsage usage={usage.data} />}
            </Rail>
          </div>
        )}
      </PageScroll>
    </Page>
  );
}
